'use client';

// A ChatGPT-style table card rendered inside the Master chat. It prefers the LIVE
// Analyst doc (docId + sheet + range) read straight from the tables store, so any
// edit made in the Analyst editor reflects here immediately (bidirectional link).
// When the live doc is NOT present in this browser (a different device, cleared
// localStorage, ...) it falls back to the static `data` snapshot embedded in the
// chat message, which is server-durable, so the table still renders everywhere.
// The presentational table + copy/download toolbar live in the shared
// ChatTableCardShell; this card adds the open-in-editor (pencil) action.
import React, { useEffect, useMemo, useState } from 'react';
import { useTablesStore } from '@/components/tables/tablesStore';
import { computeTableSlice, loadSsf, getSsf, type TableSlice } from '@/components/tables/analystTableSlice';
import { ChatTableCardShell } from '@/components/chat/ChatTableCardShell';
import { Pencil } from '@/components/ui/lucide-memo';

interface ChatAnalystTableCardProps {
  docId: string;
  sheet?: string;
  range?: string;
  title?: string;
  /** Static snapshot embedded in the (server-durable) chat marker; used when the
   *  live doc isn't available in this browser so the table still renders. */
  data?: TableSlice | null;
}

export function ChatAnalystTableCard({ docId, sheet: sheetName, range, title, data }: ChatAnalystTableCardProps) {
  const doc = useTablesStore((s) => s.docs[docId] ?? null);
  const rev = useTablesStore((s) => s.rev); // bumped on every agent / user edit → re-render live
  const [ssfReady, setSsfReady] = useState<boolean>(!!getSsf());

  useEffect(() => {
    if (getSsf()) return;
    let alive = true;
    void loadSsf().then(() => {
      if (alive) setSsfReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Prefer the live doc; fall back to the embedded snapshot. ssfReady is in the deps
  // so the live grid recomputes with proper number formats once SSF has loaded.
  const liveGrid = useMemo(
    () => (doc ? computeTableSlice(doc, sheetName, range, getSsf()) : null),
    [doc, rev, sheetName, range, ssfReady]
  );
  const grid: TableSlice | null = liveGrid ?? (data && data.header ? data : null);
  const hasLiveDoc = Boolean(doc);

  if (!grid) {
    return (
      <div style={{ margin: '10px 0', borderRadius: 12, border: '1px solid #2E2E2E', padding: '14px 16px', color: '#8E8E8E', fontSize: 13 }}>
        This table is no longer available.
      </div>
    );
  }

  const openEditor = () => useTablesStore.getState().requestOpenInEditor(docId, grid.sheetName);

  // The pencil (open-in-editor) shows only when the live doc is present in this browser
  // (the snapshot fallback has nothing to open).
  return (
    <ChatTableCardShell
      grid={grid}
      title={title}
      extraActions={
        hasLiveDoc ? (
          <button
            type="button"
            onClick={openEditor}
            title="Open in the Analyst editor"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#B5B5B5] transition-colors hover:bg-[#2A2A2A] hover:text-white"
          >
            <Pencil size={16} strokeWidth={2} />
          </button>
        ) : null
      }
    />
  );
}
